import { useMemo, useState } from 'react'
import { ActionIcon, Badge, Box, Button, Group, Stack, Text } from '@mantine/core'
import { IconPin, IconSparkles, IconX } from '@tabler/icons-react'
import { useNavigate } from '@tanstack/react-router'
import { useAgentPin, useAgentUnpin } from '../../../../api'
import { useAgentsModal, useAgentsStore } from '../../../../stores/agentsStore'
import { trackComponentInteraction } from '../../../../utils/analytics'
import classes from './AgentDetailModal.module.css'
import type { AgentDetailModalProps } from './types'


export function AgentDetailModal(_props: AgentDetailModalProps) {
  const navigate = useNavigate()
  const { agent, close } = useAgentsModal()

  const pinMutation = useAgentPin()
  const unpinMutation = useAgentUnpin()

  const [optimisticPinned, setOptimisticPinned] = useState<boolean | null>(null)

  const isPinned = optimisticPinned ?? agent?.pinned ?? false

  const prompts = useMemo(() => {
    if (!agent) return []
    const fromApi = agent.sample_prompts ?? []
    return fromApi.slice(0, 4)
  }, [agent])

  const handleClose = () => {
    close()
  }

  const handlePinToggle = () => {
    if (!agent) return
    const next = !isPinned
    setOptimisticPinned(next)

    // Track agent pinning/unpinning
    trackComponentInteraction(
      'AgentsModal',
      agent.display_name || agent.name,
      'agent',
      next ? 'pin' : 'unpin',
      next ? 'agent_pinned' : 'agent_unpinned'
    )

    const action = next ? pinMutation.mutate : unpinMutation.mutate
    action(agent.id, {
      onError: () => {
        // Revert optimistic state on error
        setOptimisticPinned(!next)
      },
    })
  }

  const handleStartChat = () => {
    if (!agent) return

    // Track starting a chat with an agent
    trackComponentInteraction(
      'AgentsModal',
      agent.display_name || agent.name,
      'agent',
      'chat',
      'agent_chat_entered'
    )

    // Populate preselected agent for chat input and navigate to home
    useAgentsStore.getState().setPreselectedAgentId(agent.id)
    // Close modal first for a smooth UX
    close()
    navigate({ to: '/' })
  }

  const handlePromptClick = (promptText: string) => {
    if (!agent) return

    // Track clicking on an example prompt
    trackComponentInteraction(
      'AgentsModal',
      agent.display_name || agent.name,
      'prompt',
      'click',
      'agent_prompt_selected'
    )

    // Preselect agent and prompt text for home chat input
    useAgentsStore.getState().setPreselectedAgentId(agent.id)
    useAgentsStore.getState().setPreselectedPromptText(promptText)
    // Close modal and navigate home
    close()
    navigate({ to: '/' })
  }

  if (!agent) {
    return null
  }

  return (
    <Box className={classes.container} aria-label="Agent details">
      <Group justify="flex-end" className={classes.header}>
        <Button
          onClick={handlePinToggle}
          variant="subtle"
          size="sm"
          classNames={{ root: classes.pinButton, label: classes.pinLabel }}
          leftSection={<IconPin size={24} className={classes.pinIcon} />}
          aria-label={isPinned ? 'Unpin agent' : 'Pin agent'}
          disabled={pinMutation.isPending || unpinMutation.isPending}
        >
          {isPinned ? 'Pinned' : 'Pin'}
        </Button>
        <ActionIcon aria-label="Close modal" onClick={handleClose} size={36} classNames={{ root: classes.closeButton }}>
          <IconX size={24} className={classes.closeIcon} />
        </ActionIcon>
      </Group>

      <Stack gap={18}>
        <Group gap={8}>
          <Text className={classes.title}>{agent.display_name || agent.name}</Text>
          {agent.tag && (
            <Badge size="sm" variant="light" color="blue" style={{ textTransform: 'capitalize' }}>
              {agent.tag}
            </Badge>
          )}
        </Group>
        <Text className={classes.description}>{agent.description}</Text>
      </Stack>

      <Stack gap={18}>
        <Text className={classes.sectionTitle}>Example Prompts</Text>
        <div className={classes.prompts}>
          {prompts.map((p, i) => (
            <div
              key={i}
              className={classes.promptCard}
              onClick={() => handlePromptClick(p)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handlePromptClick(p)
                }
              }}
              aria-label={`Use example prompt: ${p}`}
            >
              {p}
            </div>
          ))}
        </div>
      </Stack>

      <Stack gap={18}>
        <Text className={classes.sectionTitle}>Capabilities</Text>
        <div className={classes.capabilities}>
          {agent.capabilities.map((cap) => (
            <span key={cap} className={classes.capabilityChip}>
              <IconSparkles size={20} stroke={2} className={classes.capabilityIcon} />
              {cap}
            </span>
          ))}
        </div>
      </Stack>

      <div className={classes.footer}>
        <Button
          className={classes.startButton}
          size="md"
          onClick={handleStartChat}
          aria-label="Start new chat with agent"
        >
          Start New Chat
        </Button>
      </div>
    </Box>
  )
}

export default AgentDetailModal
